import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signOut, getRedirectResult } from 'firebase/auth';
import { auth } from '../firebase/config.ts';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  isProcessingRedirect: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const handleRedirectResult = async () => {
      // Check if this might be a redirect result
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const hasAuthParams = urlParams.has('apiKey') || hashParams.has('apiKey') ||
                           urlParams.has('authuser') || hashParams.has('authuser');

      if (hasAuthParams) {
        setIsProcessingRedirect(true);
      }

      try {
        // Add a small delay to ensure Firebase is fully initialized
        await new Promise(resolve => setTimeout(resolve, 100));

        const result = await getRedirectResult(auth);

        if (result && isMounted) {
          // The user will be set by onAuthStateChanged below
        }
      } catch (error: any) {
        if (isMounted) {
          console.error('AuthContext: Redirect result error:', error);
        }
      } finally {
        if (isMounted) {
          setIsProcessingRedirect(false);
        }
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!isMounted) return;

      if (firebaseUser) {
        // Check if user is password-based and not verified
        const isPasswordUser = firebaseUser.providerData.some(
          (p) => p.providerId === 'password'
        );

        if (isPasswordUser && !firebaseUser.emailVerified) {
          setUser(null);
        } else {
          setUser(firebaseUser);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    // Handle redirect result first, then set up auth state listener
    handleRedirectResult().then(() => {
      // Auth state listener is already set up above
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ user, loading, logout, isProcessingRedirect }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
